import { useState } from "react";
import { Form } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { CarFront, ChevronDown } from "react-bootstrap-icons";
import { Input } from "./Input";
import { Select } from "./Select";
import { Button } from "./Button";
import { colors } from "../Data/Common";
import { Vehicle } from "../Data/ApiTypes";
import { getStateEnumFromCode, showStateCode, showStateName, UsState } from "../Data/UsState";

function sanitizeLicensePlate( value: string ): string {
    return value.replace( /[^a-zA-Z0-9]/g, "" ).toUpperCase();
}

const states = Object.values( UsState ).filter( ( value ): value is UsState => typeof value === "number" );

export interface AddVehicleInlineProps {
    planName?:     string;
    facilityName?: string;
    onSubmit:      ( v: Vehicle ) => void;
    onDismiss:     () => void;
}

export function AddVehicleInline( { planName, facilityName, onSubmit, onDismiss }: AddVehicleInlineProps ) {
    const [showDetails, setShowDetails] = useState( false );
    const assigning = !!planName;
    const { handleSubmit, register, getValues, control, formState: { errors } }
        = useForm<Vehicle>( {
            defaultValues: {
                licensePlateNumber: "",
                stateCode: "",
                make: "",
                model: "",
                color: undefined,
            },
        } );

    function submit() {
        const vals = getValues();
        if( !showDetails ) {
            vals.make = "N/A";
            vals.model = "N/A";
            vals.color = "N/A";
        }
        onSubmit( vals );
    }

    return <div className="rounded-4 border bg-white p-3 my-3 shadow-subtle">
        <div className="fw-bold fs-5 mb-3">Add your first vehicle</div>
        {assigning && (
            <div className="rounded-3 p-3 mb-3" style={{ background: "#FDE8F3" }}>
                <div className="text-secondary small mb-2">This vehicle will be assigned to</div>
                <div className="d-flex align-items-start gap-2">
                    <div className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                         style={{ width: 28, height: 28, background: "#FC5EB4", color: "#fff", fontSize: 14 }}>
                        P
                    </div>
                    <div style={{ color: "#C43B7A" }}>
                        <div className="fw-semibold">{planName}</div>
                        {facilityName && <div className="small" style={{ opacity: 0.75 }}>{facilityName}</div>}
                    </div>
                </div>
            </div>
        )}
        <Form onSubmit={handleSubmit( submit )}>
            <Select
                label="State"
                {...register( "stateCode", {
                    required: "State is required",
                    validate: ( value ) => {
                        if( !value ) return true;
                        return getStateEnumFromCode( value ) !== undefined || "Invalid state code";
                    },
                } )}
                error={errors.stateCode}>
                <option value="">Select a state</option>
                {states.map( stateEnum => (
                    <option key={stateEnum} value={showStateCode( stateEnum )}>
                        {showStateName( stateEnum )}
                    </option>
                ) )}
            </Select>
            <Controller
                control={control}
                name="licensePlateNumber"
                rules={{ required: "License plate is required" }}
                render={( { field } ) => (
                    <Input
                        type="text"
                        label="License plate"
                        placeholder="example: ABC000"
                        formNoValidate
                        maxLength={10}
                        error={errors.licensePlateNumber}
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        {...field}
                        value={field.value ?? ""}
                        onChange={( e ) => field.onChange( sanitizeLicensePlate( e.target.value ) )}
                    />
                )}
            />
            <div className="rounded-4 border mb-3" style={{ background: "#FAFAFA", borderColor: "#ECECEC" }}>
                <button type="button"
                        className="w-100 d-flex align-items-center gap-2 border-0 bg-transparent text-start px-3 py-2"
                        onClick={() => setShowDetails( open => !open )}>
                    <CarFront size={18} className="text-secondary flex-shrink-0" />
                    <span className="flex-grow-1 min-w-0">
                        <span className="d-block fw-semibold">Vehicle details</span>
                        {!showDetails && <span className="d-block text-secondary small">Make, model and color</span>}
                    </span>
                    <span className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 small text-secondary flex-shrink-0"
                          style={{ background: "#F0F0F0" }}>
                        Optional
                        <ChevronDown size={12} style={{ transform: showDetails ? "rotate(180deg)" : undefined }} />
                    </span>
                </button>
                {showDetails && (
                    <div className="px-3 pb-3">
                        <div className="d-flex flex-row w-100 gap-3">
                            <Input className="w-100"
                                   type="text"
                                   label="Make"
                                   placeholder="example: Kia"
                                   formNoValidate
                                   {...register( "make", { required: "Make is required" } )}
                                   error={errors.make} />
                            <Input className="w-100"
                                   type="text"
                                   label="Model"
                                   placeholder="example: Sorento"
                                   formNoValidate
                                   {...register( "model", { required: "Model is required" } )}
                                   error={errors.model} />
                        </div>
                        <Controller
                            control={control}
                            name="color"
                            rules={{ required: "Vehicle color is required" }}
                            render={( { field: { onChange, value } } ) => (
                                <div className="mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span>Color</span>
                                        <span className="text-secondary small">{value ? value : "None selected"}</span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {colors.map( col => {
                                            const selected = value === col;
                                            return <button key={col}
                                                type="button"
                                                aria-label={col}
                                                className="p-0"
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: "50%",
                                                    backgroundColor: col,
                                                    border: col === "White" ? "1px solid #D0D0D0" : "1px solid transparent",
                                                    boxShadow: selected ? "0 0 0 2px #fff, 0 0 0 4px #212529" : undefined,
                                                }}
                                                onClick={() => onChange( col )} />;
                                        } )}
                                    </div>
                                    {errors.color && <Form.Text className="d-block text-danger mt-2">{errors.color.message}</Form.Text>}
                                </div>
                            )} />
                    </div>
                )}
            </div>
            <Button type="submit" className="w-100 btn-main">
                {assigning ? "Add and assign" : "Add vehicle"}
            </Button>
            <button type="button"
                    className="btn btn-link w-100 mt-1 text-secondary text-decoration-none"
                    onClick={onDismiss}>
                Not now
            </button>
        </Form>
    </div>;
}
